import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Check, ChevronDown, X } from "lucide-react";
import * as React from "react";
import { AndoLogo } from "../ando/AndoLogo";
import {
  StyleTemplatePicker,
  type StyleTemplatePickerProps,
} from "../ando/actions/StyleTemplatePicker";
import { cn } from "../lib/cn";
import { Button } from "../primitives/button";
import { Popover, PopoverContent, PopoverTrigger } from "../primitives/popover";

/**
 * DraftActionBar — slim inline action bar mounted ABOVE the editor when a
 * document is still a pending AI draft (Phase 1.1, "Editor-first canvas",
 * May 2026).
 *
 * Replaces the legacy ``FocusedActionView`` modal flow for
 * ``document_create`` proposals: the agent materialises the file at
 * proposal-time, the editor opens inline with the user already on the
 * canvas, and this 36px bar carries the rationale + destination picker +
 * Confirm/Reject CTAs. The pattern matches Notion AI / Linear AI inline
 * draft accept-bars — no modal context switch.
 *
 * DRY contract:
 *   - Cross-mime: the bar is mime-agnostic. Same component over docx /
 *     xlsx / pptx editors — the parent (``ArtifactPane``) mounts it
 *     conditionally on ``session.action_state?.draft_status === "pending_ai"``.
 *   - Pure presentational: no store, no fetcher, no router. Caller wires
 *     ``onConfirm`` / ``onReject`` to the BE finalize/reject mutations.
 *   - Single ``busy`` flag short-circuits BOTH CTAs (we never want a
 *     half-rejected / half-finalized state).
 *
 * Brand-system (Ando May 2026): cream/orange palette, hairline borders,
 * lucide 1.75 stroke, ``AndoLogo`` eyebrow (the AI signifier — never
 * ``Sparkles``), primary orange filled Confirm CTA, ghost Reject CTA.
 *
 * Accessibility:
 *   - ``role="region"`` + ``aria-label`` so SR users know what the bar
 *     governs.
 *   - ``aria-live="polite"`` on the status announcement region so the
 *     ``busy`` flip + future failure copy reach SR users.
 *   - Destination dropdown uses the shared Radix Popover (keyboard +
 *     ESC + focus-trap baked in).
 */

export interface Destination {
  /** Stable id matching ``EditSessionActionState.destinations[].id``. */
  readonly id: string;
  /** Human-readable label rendered in the trigger + dropdown. */
  readonly label: string;
  /** Lucide icon to render. */
  readonly icon: React.ReactNode;
  /** Greyed-out + non-pickable when true (provider not connected). */
  readonly disabled?: boolean;
}

export interface DraftActionBarProps {
  /** Server-issued proposal id — surfaced for debugging / test selectors. */
  readonly proposalId: string;
  /**
   * One-line rationale shown next to the eyebrow icon. Truncated at
   * ``max-w-[40ch]`` to keep the bar at 36px height regardless of
   * agent-emitted copy length.
   */
  readonly rationale: string;
  /** Save targets the user can pick from. ``length>=1`` required. */
  readonly destinations: ReadonlyArray<Destination>;
  /** Currently selected destination id. Must match one of ``destinations[].id``. */
  readonly selectedDestinationId: string;
  /** Confirm CTA — caller awaits the BE ``/actions/{id}/finalize`` mutation. */
  readonly onConfirm: () => Promise<void>;
  /** Reject CTA — caller awaits the BE ``/actions/{id}/reject`` mutation. */
  readonly onReject: () => Promise<void>;
  /** Destination changed in the dropdown — caller persists locally. */
  readonly onDestinationChange: (id: string) => void;
  /**
   * When true: spinner on Conferma + every interactive element disabled.
   * Owned by the parent (single source of truth across both mutations).
   */
  readonly busy?: boolean;
  /**
   * Optional "Aspetto" (style / template / Brand Kit) picker — same
   * ``StyleTemplatePicker`` the FocusedActionView card uses, surfaced here as
   * a popover so the editor-canvas path reaches feature-parity with the card
   * (the user can choose the look on EITHER surface). Built by the parent from
   * ``useDocumentCreateStyleTemplate`` for ``document_create`` drafts; omitted
   * for plain edits. The chosen value is read back via the hook's
   * ``buildModifications()`` and threaded into the finalize call by the parent.
   */
  readonly styleTemplate?: StyleTemplatePickerProps;
  /** Optional extra class on the outer ``<section>``. */
  readonly className?: string;
}

export const DraftActionBar: React.FC<DraftActionBarProps> = ({
  proposalId,
  rationale,
  destinations,
  selectedDestinationId,
  onConfirm,
  onReject,
  onDestinationChange,
  busy = false,
  styleTemplate,
  className,
}) => {
  const { i18n } = useLingui();
  const [picking, setPicking] = React.useState(false);
  const [pickingStyle, setPickingStyle] = React.useState(false);
  // Short label for the Aspetto trigger — derived from the current selection.
  const aspettoLabel = styleTemplate?.value?.templateSlug
    ? i18n._(msg`Template`)
    : styleTemplate?.value?.styleId
      ? i18n._(msg`Stile`)
      : i18n._(msg`Brand Kit`);
  // Local "in-flight" flag for the click handlers — combines with the
  // parent ``busy`` to disable controls while the awaited promise
  // resolves. Without this, double-click on Conferma would dispatch two
  // finalize calls before the parent flips ``busy`` true.
  const [pending, setPending] = React.useState<"confirm" | "reject" | null>(null);
  const disabled = busy || pending !== null;

  const selected = React.useMemo(
    () => destinations.find((d) => d.id === selectedDestinationId) ?? destinations[0],
    [destinations, selectedDestinationId],
  );

  const handleConfirm = React.useCallback(async () => {
    if (disabled) return;
    setPending("confirm");
    try {
      await onConfirm();
    } finally {
      setPending(null);
    }
  }, [disabled, onConfirm]);

  const handleReject = React.useCallback(async () => {
    if (disabled) return;
    setPending("reject");
    try {
      await onReject();
    } finally {
      setPending(null);
    }
  }, [disabled, onReject]);

  const handlePickDestination = React.useCallback(
    (id: string) => {
      onDestinationChange(id);
      setPicking(false);
    },
    [onDestinationChange],
  );

  return (
    <section
      aria-label={i18n._(msg`Bozza AI da confermare`)}
      data-proposal-id={proposalId}
      className={cn(
        "flex h-9 shrink-0 items-center gap-2 border-hairline border-b bg-ando-orange-soft/55 px-3",
        className,
      )}
    >
      {/* Eyebrow — orange sparkle + uppercase brand tag + truncated rationale */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <AndoLogo size={12} className="shrink-0" aria-hidden />
        <span className="font-medium text-[10.5px] text-ando-orange uppercase tracking-[0.06em]">
          {i18n._(msg`Bozza AI`)}
        </span>
        <span aria-hidden className="h-3 w-px shrink-0 bg-ando-orange/35" />
        <p className="min-w-0 max-w-[40ch] truncate text-[11.5px] text-ink-1" title={rationale}>
          {rationale}
        </p>
      </div>

      {/* Aspetto (style / template) + Destination + CTAs */}
      <div className="flex shrink-0 items-center gap-2">
        {/* Aspetto popover — parity with the FocusedActionView card so the
            user can choose Stile / Template / Brand Kit on the editor canvas
            too. Reuses the same StyleTemplatePicker (no duplication). */}
        {styleTemplate ? (
          <>
            <span className="hidden font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em] sm:inline">
              {i18n._(msg`Aspetto`)}
            </span>
            <Popover open={pickingStyle} onOpenChange={setPickingStyle}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={i18n._(
                    msg`Aspetto del documento (attuale: ${aspettoLabel})`,
                  )}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white/85 px-2 py-1 font-medium text-[11.5px] text-ink-1 transition-colors",
                    "hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  <AndoLogo size={12} className="shrink-0" aria-hidden />
                  <span className="max-w-[12ch] truncate">{aspettoLabel}</span>
                  <ChevronDown className="h-3 w-3 text-ink-3" aria-hidden strokeWidth={1.75} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={6}
                className="max-h-[60vh] w-[440px] overflow-auto p-3"
              >
                <StyleTemplatePicker {...styleTemplate} />
              </PopoverContent>
            </Popover>
          </>
        ) : null}

        <span className="hidden font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em] sm:inline">
          {i18n._(msg`Salva in`)}
        </span>
        <Popover open={picking} onOpenChange={setPicking}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={i18n._(
                msg`Destinazione di salvataggio (attuale: ${selected?.label ?? "—"})`,
              )}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border border-hairline bg-white/85 px-2 py-1 font-medium text-[11.5px] text-ink-1 transition-colors",
                "hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <span className="inline-flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-ink-2">
                {selected?.icon}
              </span>
              <span className="max-w-[14ch] truncate">{selected?.label ?? "—"}</span>
              <ChevronDown className="h-3 w-3 text-ink-3" aria-hidden strokeWidth={1.75} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={6} className="w-56 p-1">
            <ul role="menu" aria-label={i18n._(msg`Destinazioni disponibili`)} className="space-y-0.5">
              {destinations.map((d) => {
                const isSelected = d.id === selected?.id;
                return (
                  <li key={d.id} role="presentation">
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={isSelected}
                      disabled={d.disabled}
                      onClick={() => handlePickDestination(d.id)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] transition-colors",
                        isSelected
                          ? "bg-ando-orange/10 text-ando-orange"
                          : "text-ink-1 hover:bg-ink-1/[0.04]",
                        d.disabled && "cursor-not-allowed opacity-50",
                      )}
                    >
                      <span className="inline-flex items-center gap-2 [&>svg]:h-3.5 [&>svg]:w-3.5">
                        {d.icon}
                      </span>
                      <span className="flex-1 truncate">{d.label}</span>
                      {isSelected ? (
                        <Check className="h-3 w-3 text-ando-orange" aria-hidden strokeWidth={2} />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </PopoverContent>
        </Popover>

        {/* Reject — ghost */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleReject}
          disabled={disabled}
          aria-label={i18n._(msg`Rifiuta la bozza AI`)}
        >
          <X className="h-3 w-3" aria-hidden strokeWidth={1.75} />
          {i18n._(msg`Rifiuta`)}
        </Button>

        {/* Confirm — primary orange filled */}
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleConfirm}
          disabled={disabled}
          aria-label={i18n._(msg`Conferma e salva la bozza`)}
          // Reserve room for the spinner so the button width doesn't jump
          // when ``pending === "confirm"`` flips.
          className="min-w-[92px] justify-center"
        >
          {pending === "confirm" || busy ? (
            <Spinner />
          ) : (
            <Check className="h-3 w-3" aria-hidden strokeWidth={2} />
          )}
          {i18n._(msg`Conferma`)}
        </Button>
      </div>

      {/* SR-only status announcer */}
      <span aria-live="polite" className="sr-only">
        {pending === "confirm" || busy
          ? i18n._(msg`Salvataggio della bozza in corso`)
          : pending === "reject"
            ? i18n._(msg`Rifiuto della bozza in corso`)
            : ""}
      </span>
    </section>
  );
};
DraftActionBar.displayName = "DraftActionBar";

const Spinner: React.FC = () => {
  const { i18n } = useLingui();
  // 12px circular spinner — matches the button icon slot ([&_svg]:size-3
  // on size="sm"). No framer-motion: the bar is on the hot path of every
  // editor open + reduced-motion users still need a visible busy hint.
  // The visual busy state is also announced via the parent's sr-only
  // aria-live region; the <title> below is the inline SVG fallback for
  // SR tools that read SVG content directly.
  return (
  <svg
    className="h-3 w-3 animate-spin text-white/85"
    viewBox="0 0 24 24"
    role="img"
    aria-label={i18n._(msg`In corso`)}
  >
    <title>{i18n._(msg`In corso`)}</title>
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeOpacity="0.3"
      strokeWidth="3"
      fill="none"
    />
    <path
      d="M22 12a10 10 0 0 1-10 10"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
  );
};
